/**
 * AsignarAreasModal
 * Modal para asignar áreas de un presupuesto
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiCheck } from 'react-icons/fi';
import { areaService } from '../services/catalogoService';
import { presupuestoVentaAreaService, presupuestoCostoAreaService } from '../services/onebaseService';
import './AsignarAreasModal.css';

import Loading from './Loading';
const AsignarAreasModal = ({ isOpen, presupuestoId, tipo = 'venta', cuentaId, onClose, onSuccess }) => {
  const [areas, setAreas] = useState([]);
  const [areasAsignadas, setAreasAsignadas] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const areaService_ = tipo === 'venta' ? presupuestoVentaAreaService : presupuestoCostoAreaService;

  useEffect(() => {
    if (isOpen && presupuestoId && cuentaId) {
      cargarDatos();
    }
  }, [isOpen, presupuestoId, cuentaId, tipo]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar todas las áreas disponibles
      const areasResponse = await areaService.getAll({
        cuenta: cuentaId,
        status: 'activo'
      });
      const areasData = Array.isArray(areasResponse.data) ? areasResponse.data : (areasResponse.data.results || []);
      setAreas(areasData);

      // Cargar áreas ya asignadas
      const filtro = tipo === 'venta'
        ? { presupuesto_venta: presupuestoId }
        : { presupuesto_costo: presupuestoId };

      const asignacionesResponse = await areaService_.getAll(filtro);
      const asignacionesData = Array.isArray(asignacionesResponse.data)
        ? asignacionesResponse.data
        : (asignacionesResponse.data.results || []);

      setAreasAsignadas(asignacionesData);

      // Marcar las áreas que ya están asignadas
      const idsAsignados = new Set(asignacionesData.map(a => a.area));
      setSeleccionadas(idsAsignados);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar áreas');
    } finally {
      setLoading(false);
    }
  };

  const toggleArea = (areaId) => {
    const nuevaSeleccion = new Set(seleccionadas);
    if (nuevaSeleccion.has(areaId)) {
      nuevaSeleccion.delete(areaId);
    } else {
      nuevaSeleccion.add(areaId);
    }
    setSeleccionadas(nuevaSeleccion);
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      // Identificar áreas a agregar y eliminar
      const areasParaAgregar = Array.from(seleccionadas).filter(
        areaId => !areasAsignadas.some(a => a.area === areaId)
      );
      const areasParaEliminar = areasAsignadas.filter(
        a => !seleccionadas.has(a.area)
      );

      // Eliminar asignaciones que ya no están seleccionadas
      for (const asignacion of areasParaEliminar) {
        await areaService_.delete(asignacion.id);
      }

      // Agregar nuevas asignaciones
      for (const areaId of areasParaAgregar) {
        const datos = tipo === 'venta'
          ? { presupuesto_venta: presupuestoId, area: areaId }
          : { presupuesto_costo: presupuestoId, area: areaId };

        await areaService_.create(datos);
      }

      toast.success('Áreas asignadas correctamente');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al guardar asignaciones:', error);
      toast.error('Error al guardar asignaciones');
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="asignar-areas-overlay">
      <div className="asignar-areas-modal">
        <div className="asignar-areas-header">
          <h3>Asignar Áreas al Presupuesto</h3>
          <button onClick={onClose} className="close-btn">
            <FiX />
          </button>
        </div>

        <div className="asignar-areas-content">
          {loading ? (
            <Loading fullScreen={false} size="sm" mensaje="Cargando áreas" />
          ) : areas.length === 0 ? (
            <div className="empty-state">
              <p>No hay áreas disponibles en el catálogo</p>
            </div>
          ) : (
            <div className="areas-grid">
              {areas.map(area => (
                <div
                  key={area.id}
                  className={`area-card ${seleccionadas.has(area.id) ? 'selected' : ''}`}
                >
                  <div className="area-card-header">
                    <h4>{area.nombre}</h4>
                    <input
                      type="checkbox"
                      checked={seleccionadas.has(area.id)}
                      onChange={() => toggleArea(area.id)}
                      className="area-checkbox"
                    />
                  </div>
                  {area.descripcion && (
                    <p className="area-descripcion">{area.descripcion}</p>
                  )}
                  <div className="area-stats">
                    <span className="stat">
                      📦 {area.partidas?.length || 0} partidas
                    </span>
                    <span className="stat">
                      🔧 {area.partidas?.reduce((total, p) => total + (p.recursos?.length || 0), 0) || 0} recursos
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            className="btn btn-primary"
            disabled={guardando || loading}
          >
            {guardando ? 'Guardando...' : `Asignar (${seleccionadas.size})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AsignarAreasModal;
